import Markdown from 'markdown-to-jsx';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import type {
  AnswerDetails,
  AnswerFeedback as Feedback,
  ChatMessage,
} from '../../types';
import { useShowSources } from '../../hooks/useShowSources';
import { splitSources } from '../../utils/answerSources';
import { fillTemplate } from '../../utils/formatters';
import defaults from '../../translations/en.json';
import { AnswerFeedback } from './AnswerFeedback';
import { responseDetailLabel } from './DetailSlider';

const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: ${(props) => (props.$role === 'user' ? 80 : 88)}%;
  align-self: ${(props) =>
    props.$role === 'user' ? 'flex-end' : 'flex-start'};
  padding: 7px 10px;
  border-radius: ${(props) =>
    props.$role === 'user' ? '8px 8px 2px 8px' : '8px 8px 8px 2px'};
  font-size: 1rem;
  line-height: 1.4;
  white-space: ${(props) => (props.$role === 'user' ? 'pre-wrap' : 'normal')};
  word-break: break-word;
  /* MAIA answers are markdown: tighten browser margins to fit the bubble */
  p,
  ul,
  ol {
    margin: 0 0 0.6em;
  }
  ul,
  ol {
    padding-left: 1.4em;
  }
  li > ul,
  li > ol {
    margin-bottom: 0;
  }
  h1,
  h2,
  h3,
  h4 {
    margin: 0.8em 0 0.4em;
    font-size: 1.05em;
    font-weight: 600;
  }
  code {
    color: inherit;
    background: none;
  }
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  background-color: ${(props) =>
    props.$role === 'user'
      ? props.theme.palette.primary.main
      : props.theme.palette.background.chatBubble ||
        props.theme.palette.background.dark1 ||
        props.theme.palette.background.default};
  color: ${(props) => {
    if (props.$role !== 'user') {
      return (
        props.theme.palette.text.chatBubble || props.theme.palette.text.primary
      );
    }
    return props.theme.palette.type === 'dark'
      ? props.theme.palette.common.black
      : props.theme.palette.common.white;
  }};
`;

/** The answer's Sources section, under a divider, shown only when the header toggle is on. */
const SourcesBlock = styled.div`
  margin-top: 0.6em;
  padding-top: 0.5em;
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  font-size: 0.875rem;
  color: ${(props) =>
    props.theme.palette.text.sources || props.theme.palette.text.primary};
  && ul {
    margin: 0.2em 0 0;
  }
`;

const SourcesTitle = styled.div`
  font-weight: 600;
`;

/** A rateable answer and its thumbs; hovering it shows an older answer's thumbs. */
const AnswerBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  &:hover .maia-feedback-row {
    opacity: 1;
  }
`;

const CancelledMark = styled.span`
  align-self: flex-end;
  margin-top: -4px;
  font-size: 0.7rem;
  font-style: italic;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
`;

const TypingRow = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.72rem;
  background-color: ${(props) =>
    props.theme.palette.background.dark1 ||
    props.theme.palette.background.default};
  color: ${(props) => props.theme.palette.text.secondary};
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.palette.primary.main};
    animation: ${pulse} 1.2s infinite ease-in-out;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

/** 'Response length: Verbose' as its own paragraph under a stored answer's text; null when the level is unknown. */
const responseLengthSuffix = (answer: AnswerDetails) => {
  const level = responseDetailLabel(answer.responseDetail);
  return level ? (
    <p>{fillTemplate(defaults.responseLength, { level })}</p>
  ) : null;
};

/** Raw HTML in an answer shows as text instead of being rendered. */
const markdownOptions = { disableParsingRawHTML: true, forceBlock: true };

/** An answer as markdown, then `suffix`, then its Sources section when the toggle is on. */
const AnswerText = ({
  content,
  suffix,
  showSources,
}: {
  content: string;
  suffix: React.ReactNode;
  showSources: boolean;
}) => {
  const { body, sources } = splitSources(content);
  return (
    <>
      <Markdown options={markdownOptions}>{body}</Markdown>
      {suffix}
      {showSources && sources ? (
        <SourcesBlock data-testid="maia-sources">
          <SourcesTitle>{defaults.sourcesTitle}</SourcesTitle>
          <Markdown options={markdownOptions}>{sources}</Markdown>
        </SourcesBlock>
      ) : null}
    </>
  );
};

type MessageListProps = {
  messages: ChatMessage[];
  /** Shows the three-dot indicator; `typingLabel` adds text beside it. */
  isTyping?: boolean;
  typingLabel?: string | null;
  /** Rendered first (e.g. the past-conversation summary chip). */
  header?: React.ReactNode;
  /** Rendered last, under the typing indicator (e.g. the slow-reply cancel prompt). */
  footer?: React.ReactNode;
  /** Live chat only: thumbs under each answer the gateway can rate. */
  onRateAnswer?: (
    messageId: string,
    feedback: Feedback | null,
  ) => Promise<boolean>;
};

/** Chat bubbles (user right, MAIA left) that keep the latest message in view. */
export const MessageList = ({
  messages,
  isTyping,
  typingLabel,
  header,
  footer,
  onRateAnswer,
}: MessageListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [showSources] = useShowSources();
  const hasFooter = Boolean(footer);

  useEffect(() => {
    const node = listRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages.length, isTyping, typingLabel, hasFooter]);

  const latestAnswerId = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant')?.id;

  return (
    <List ref={listRef} role="log" aria-live="polite">
      {header}
      {messages.map((message) => {
        const bubble = (
          <Bubble
            $role={message.role}
            data-role={message.role}
            data-testid="maia-message"
            style={message.cancelled ? { opacity: 0.6 } : undefined}
          >
            {message.role === 'assistant' ? (
              <AnswerText
                content={message.content}
                suffix={
                  message.answer ? responseLengthSuffix(message.answer) : null
                }
                showSources={showSources}
              />
            ) : (
              message.content
            )}
          </Bubble>
        );
        if (onRateAnswer && message.requestId != null) {
          return (
            <AnswerBlock key={message.id}>
              {bubble}
              <AnswerFeedback
                feedback={message.feedback}
                isLatest={message.id === latestAnswerId}
                onRate={(feedback) => onRateAnswer(message.id, feedback)}
              />
            </AnswerBlock>
          );
        }
        return (
          <React.Fragment key={message.id}>
            {bubble}
            {message.cancelled ? (
              <CancelledMark>{defaults.questionCancelled}</CancelledMark>
            ) : null}
          </React.Fragment>
        );
      })}
      {isTyping ? (
        <TypingRow
          role="status"
          aria-label={typingLabel || defaults.assistantTyping}
        >
          <Dots aria-hidden="true">
            <span />
            <span />
            <span />
          </Dots>
          {typingLabel ? <span>{typingLabel}</span> : null}
        </TypingRow>
      ) : null}
      {footer}
    </List>
  );
};

export default MessageList;
